import React, { useState, useEffect, useRef } from 'react';
import axios, { AxiosError, AxiosResponse } from 'axios';
import Skeleton from './Skeleton';
import { useAppContext } from "../../../AppDataContext";

const Visualisation: React.FC = () => {
  const [data, setData] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const elementRef = useRef<HTMLDivElement>(null);
  const [widthInframe, setWidthIframe] = useState<string>("100%");
  const [containerHeight, setContainerHeight] = useState<string>("480px"); // You can adjust as needed
  const AppDataContext = useAppContext();

  // Extract IDs
  const clientId = AppDataContext.client_id;
  const userId = AppDataContext.userId;
  const messageId = sessionStorage.getItem("message_id") || "";
  const sessionId = sessionStorage.getItem("session_ID") || "";
  const vizId =
    [...(AppDataContext.actionDataContextProps || [])]
      .reverse()
      .find(
        item =>
          item?.action?.includes("VIZ_I_FRAME") &&
          item?.actionKey === messageId
      )?.inputs?.viz_be_handle ?? "";

  // Responsive width & height logic
  useEffect(() => {
    const updateDimensions = () => {
      const windowWidth = window.innerWidth;
      if (windowWidth < 600) {
        setWidthIframe("calc(100vw - 24px)");
        setContainerHeight("270px");
      } else if (windowWidth < 1100) {
        setWidthIframe("calc(100vw - 180px)");
        setContainerHeight("340px");
      } else {
        setWidthIframe("900px");
        setContainerHeight("480px");
      }
    };
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Fetch chart HTML
  const fetchVisualisation = async (): Promise<void> => {
    setLoading(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000);

    try {
      const response: AxiosResponse<any> = await axios.get(AppDataContext.proxy + '/visualization', {
        params: {
          clientId,
          userId,
          sessionId,
          messageId,
          viz_id: vizId,
        },

        headers: {
          'Content-Type': 'application/json',
            'Authorization': `Bearer ${AppDataContext.client_token}`
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      const html_text = response.data.chart_html;
      if (html_text !== data) setData(html_text);
      setError(null);
    } catch (err) {
      clearTimeout(timeoutId);
      const axiosError = err as AxiosError<any>;
      if (axiosError.name === 'AbortError') {
        setError("Request timed out");
        return;
      }
      setError(
        axiosError.response?.data?.message ||
        axiosError.message ||
        'Failed to fetch visualisation'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId && userId && sessionId && messageId && vizId) {
      fetchVisualisation();
    }
    // eslint-disable-next-line
  }, [vizId]);

  return (
    <div
      ref={elementRef}
      style={{
        width: widthInframe,
        minWidth: 0,
        maxWidth: "100%",
        height: containerHeight,
        margin: 0,
        padding: 0,
        display: 'block',
        background: "transparent",
      }}
    >
      <div
        className="visualisation-container"
        style={{
          width: "100%",
          height: "100%",
          margin: 0,
          padding: 0,
          display: "block",
          overflow: "hidden",
          background: "transparent",
        }}
      >
        <div
          className="visualisation-container__widget"
          title="Dynamic Visualisation"
          style={{
            width: '100%',
            height: '100%',
            border: "none",
            display: 'block',
            background: "transparent",
          }}
        >
          {loading && <Skeleton height="100%" width="100%" />}
          {error && !loading && <p className="text-red-500">{error}</p>}
          {data && !loading && (
            <iframe
              key={vizId}
              className="w-full border-none shadow-none"
              srcDoc={data}
              title="Visualization"
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                background: "transparent",
                overflow: 'hidden',
                display: "block",
              }}
            />
          )}
          {!loading && !error && !data && (
            <p className="text-gray-500"></p>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(Visualisation);
